import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TextBox } from '@progress/kendo-react-inputs';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { produce } from 'immer';
import React, { useMemo, useState } from 'react';
import { useBlocker } from 'react-router';
import { v4 } from 'uuid';
import { AddOutline, FilterOutline, InfoCircleOutline, SaveOutline, TrashOutline } from '../../../../../icons/icons';
import { CustomEmailAlert } from '../../../../../types/settings';
import { DropdownListWithValueField } from '../../../../components/inputs/DropdownListWithValueField';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store-hooks';
import { setSetting } from '../../../../state/slices/settings-slice';
import { SettingsNavTitle, useSettingsPageWidth } from '../../context/SettingsNavContext';

type Item = { id: string; valid: boolean } & CustomEmailAlert;

// eslint-disable-next-line no-useless-escape
const emailRegex = new RegExp(/^(?!\.)(?!.*\.\.)([a-z0-9_'+\-\.]*)[a-z0-9_+\-]@([a-z0-9][a-z0-9\-]*\.)+[a-z]{2,}$/i);

export function SettingsCustomEmailAlertingElement() {
    useSettingsPageWidth('700px');
    const canEdit = useAppSelector((x) => x.user.rights.alertCustomEmail);
    const customEmails = useAppSelector((x) => x.settings.alertCustomEmailAlerts);
    const [edited, setEdited] = React.useState(false);
    const [edit, setEdit] = useState<Item | null>(null);
    const [emails, setEmails] = useState<Item[]>(() => customEmails.map((x) => ({ ...x, id: v4(), valid: emailRegex.test(x.email) })));
    const blocker = useBlocker(edited);

    const dispatch = useAppDispatch();

    const handleAdd = () => {
        setEdited(true);
        setEmails([...emails, { id: v4(), email: '', valid: true, aircraftAlerts: [], airportAlerts: [] }]);
    };

    const handleRemove = (id: string) => {
        setEdited(true);
        setEmails(emails.filter((x) => x.id !== id));
    };

    const handleEdit = (item: Item) => {
        setEdited(true);
        const idx = emails.findIndex((x) => x.id === item.id);
        if (idx !== -1) {
            setEmails([...emails.slice(0, idx), item, ...emails.slice(idx + 1)]);
        }
    };

    const save = () => {
        const validated = emails.map((x) => ({ ...x, valid: emailRegex.test(x.email) }));
        setEmails(validated);

        if (validated.some((x) => !x.valid)) {
            return false;
        }

        dispatch(
            setSetting(
                'alertCustomEmailAlerts',
                emails.map((x) => ({
                    email: x.email,
                    aircraftAlerts: x.aircraftAlerts,
                    airportAlerts: x.airportAlerts
                }))
            )
        );
        setEdited(false);
        return true;
    };

    return (
        <div>
            {blocker.state === 'blocked' && (
                <Dialog title="Custom emails" modal={true} onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar layout="stretched">
                        <Button
                            themeColor={'primary'}
                            onClick={() => {
                                if (save()) {
                                    blocker.proceed();
                                } else {
                                    blocker.reset();
                                }
                            }}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Custom emails</div>
                    {canEdit ? (
                        <Button svgIcon={SaveOutline} fillMode="outline" disabled={!edited} onClick={save}>
                            Save
                        </Button>
                    ) : (
                        <div style={{ height: 30 }} className="d-flex align-items-center secondary-text">
                            This page is readonly.
                        </div>
                    )}
                </div>
            </SettingsNavTitle>
            <div className="k-card p-2 p-3">
                <div className="d-flex align-items-center justify-content-between">
                    <div>Alternative emails (max 5):</div>
                    <Button
                        svgIcon={AddOutline}
                        themeColor="primary"
                        disabled={!canEdit || emails.length >= 5}
                        onClick={handleAdd}></Button>
                </div>
                {emails.map((x, i) => (
                    <div key={x.id} className="pt-2 d-flex gap-2 align-items-center">
                        <div style={{ width: '1.5ch' }}>{i + 1}.</div>
                        <div className="flex-grow-1">
                            <TextBox
                                disabled={!canEdit}
                                placeholder="email@example.com"
                                value={x.email}
                                valid={x.valid}
                                onChange={(e) => handleEdit({ ...x, email: e.value as string })}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-1" style={{ width: 350 }}>
                            <div className="flex-grow-1 text-center">
                                {x.aircraftAlerts.length} aircraft thresholds, {x.airportAlerts.length} airport thresholds
                            </div>
                            <Button
                                svgIcon={canEdit ? FilterOutline : InfoCircleOutline}
                                onClick={() => setEdit(x)}
                                fillMode="outline"></Button>
                            <Button
                                disabled={!canEdit}
                                svgIcon={TrashOutline}
                                themeColor="error"
                                fillMode="outline"
                                onClick={() => handleRemove(x.id)}></Button>
                        </div>
                    </div>
                ))}

                {emails.length === 0 && <div className="secondary-text">No custom emails defined.</div>}

                {edit && <EmailAlertFilterEditWindow onClose={() => setEdit(null)} item={edit} onEdit={handleEdit} />}
            </div>
        </div>
    );
}

function EmailAlertFilterEditWindow(props: { onClose?: () => void; item: Item; onEdit?: (item: Item) => void }) {
    const displayOrderAircraft = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.displayOrder);
    const definitionAircraft = useAppSelector((x) => x.global.monitorSettings.thresholds.aircraft.definitions);

    const displayOrderAirport = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.displayOrder);
    const definitionAirport = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.definitions);

    const aircraftThresholdOptions = useMemo(
        () =>
            displayOrderAircraft.map((x) => ({
                value: x,
                text: definitionAircraft[x].name
            })),
        [displayOrderAircraft, definitionAircraft]
    );
    const airportThresholdOptions = useMemo(
        () =>
            displayOrderAirport.map((x) => ({
                value: x,
                text: definitionAirport[x].name
            })),
        [displayOrderAirport, definitionAirport]
    );

    const canEdit = useAppSelector((x) => x.user.rights.alertCustomEmail);
    const [aircraftAlerts, setAircraftAlerts] = useState(() => props.item.aircraftAlerts.map((x) => ({ id: v4(), value: x })));
    const [airportAlerts, setAirportAlerts] = useState(() => props.item.airportAlerts.map((x) => ({ id: v4(), value: x })));
    const [selectedTab, setSelectedTab] = React.useState(0);

    return (
        <Dialog title="Edit email filter" onClose={props.onClose} width={'min(550px, 90vw)'} height={'min(800px, 90vh)'}>
            <div className="h-100 overflow-hidden d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center">
                    <div>Only alerts from the following thresholds will be sent to this email</div>
                    <Button
                        disabled={!canEdit}
                        themeColor="primary"
                        svgIcon={AddOutline}
                        onClick={() => {
                            if (selectedTab === 0) {
                                setAircraftAlerts([...aircraftAlerts, { id: v4(), value: 1 }]);
                            } else {
                                setAirportAlerts([...airportAlerts, { id: v4(), value: 1 }]);
                            }
                        }}></Button>
                </div>

                <div className="flex-grow-1 min-h-0">
                    <TabStrip
                        className="h-100 overflow-auto"
                        selected={selectedTab}
                        onSelect={(e) => setSelectedTab(e.selected)}
                        animation={false}>
                        <TabStripTab title="Aircraft" contentClassName="p-2">
                            <div>
                                {aircraftAlerts.map((x) => (
                                    <div key={x.id} className="d-flex justify-content-between pt-2 gap-2 align-items-center">
                                        <DropdownListWithValueField
                                            disabled={!canEdit}
                                            data={aircraftThresholdOptions}
                                            value={x.value}
                                            onChange={(e) =>
                                                setAircraftAlerts(
                                                    produce(aircraftAlerts, (y) => {
                                                        const idx = y.findIndex((z) => z.id === x.id);
                                                        if (idx > -1) {
                                                            y[idx] = {
                                                                ...y[idx],
                                                                value: e.value
                                                            };
                                                        }
                                                    })
                                                )
                                            }
                                        />
                                        <Button
                                            disabled={!canEdit}
                                            svgIcon={TrashOutline}
                                            themeColor="error"
                                            fillMode="outline"
                                            onClick={() => setAircraftAlerts(aircraftAlerts.filter((y) => y.id !== x.id))}></Button>
                                    </div>
                                ))}
                                {aircraftAlerts.length === 0 && <div className="secondary-text">No alerts.</div>}
                            </div>
                        </TabStripTab>
                        <TabStripTab title="Airport" contentClassName="p-2">
                            <div>
                                {airportAlerts.map((x) => (
                                    <div key={x.id} className="d-flex justify-content-between pt-2 gap-2 align-items-center">
                                        <DropdownListWithValueField
                                            disabled={!canEdit}
                                            data={airportThresholdOptions}
                                            value={x.value}
                                            onChange={(e) =>
                                                setAirportAlerts(
                                                    produce(airportAlerts, (y) => {
                                                        const idx = y.findIndex((z) => z.id === x.id);
                                                        if (idx > -1) {
                                                            y[idx] = {
                                                                ...y[idx],
                                                                value: e.value
                                                            };
                                                        }
                                                    })
                                                )
                                            }
                                        />
                                        <Button
                                            disabled={!canEdit}
                                            svgIcon={TrashOutline}
                                            themeColor="error"
                                            fillMode="outline"
                                            onClick={() => setAirportAlerts(airportAlerts.filter((y) => y.id !== x.id))}></Button>
                                    </div>
                                ))}
                                {airportAlerts.length === 0 && <div className="secondary-text">No alerts.</div>}
                            </div>
                        </TabStripTab>
                    </TabStrip>
                </div>
            </div>

            <DialogActionsBar>
                {canEdit && (
                    <Button
                        onClick={() => {
                            props.onEdit?.({
                                ...props.item,
                                aircraftAlerts: aircraftAlerts.map((x) => x.value),
                                airportAlerts: airportAlerts.map((x) => x.value)
                            });
                            props.onClose?.();
                        }}
                        themeColor="primary">
                        Save
                    </Button>
                )}
                <Button onClick={props.onClose}>{canEdit ? 'Cancel' : 'Close'}</Button>
            </DialogActionsBar>
        </Dialog>
    );
}
